import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PencilIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { HsePageChrome, useHseBusyAction } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  excludeRiskAssessment,
  exportRiskAssessmentPdf,
  fetchRiskAssessment,
  fetchRiskTiltak,
} from "../../../../api/hse/risk";
import { RiskLevelBadge } from "../../../../components/dashboard/hse/risk/RiskLevelBadge";
import { HseFieldInfoLabel } from "../../../../components/dashboard/hse/HseFieldInfoLabel";
import {
  formatDisplayDate,
  isExcluded,
  riskApiError,
  riskShowBackTo,
} from "../../../../components/dashboard/hse/risk/riskUtils";

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{value}</p>
    </div>
  );
}

export const HseKsRiskView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [record, setRecord] = useState(null);
  const [tiltak, setTiltak] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { run, busyProps } = useHseBusyAction();
  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const item = await fetchRiskAssessment(id);
      setRecord(item);
      const linked = await fetchRiskTiltak(id).catch(() => item.tiltak || []);
      setTiltak(linked);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleExclude = async () => {
    try {
      await excludeRiskAssessment(id);
      toast.success(t("hse.risk.excluded_saved"));
      load();
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.save_failed"));
    }
  };

  return (
    <HsePageChrome
      title={record?.title || t("hse.risk.view.title")}
      subtitle={t("hse.risk.view.subtitle")}
      backTo={riskShowBackTo(record)}
      mainButton={{
        label: t("hse.risk.edit"),
        icon: PencilIcon,
        path: `/dashboard/hse-ks/risk/${id}/edit`,
        hidden: !canEdit || loading || !!error,
      }}
      actions={[
        {
          label: t("hse.risk.pdf"),
          icon: PrinterIcon,
          action: () =>
            run("pdf", async () => {
              try {
                await exportRiskAssessmentPdf(id, `hse-risiko-${id}.pdf`);
                toast.success(t("hse.risk.pdf_ready"));
              } catch (err) {
                toast.error(err.message || t("hse.risk.pdf_failed"));
              }
            }),
          hidden: loading || !!error || !record,
          menu: false,
          ...busyProps("pdf"),
        },
      ]}
    >
      <div className="mx-auto mt-8 max-w-3xl px-4 sm:px-6 lg:px-8">
        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <ResourceError
            variant={!record ? "not_found" : "load_error"}
            errors={[error]}
            onRetry={load}
            backTo="/dashboard/hse-ks/risk/register"
          />
        ) : (
          <div className="space-y-6">
            <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <RiskLevelBadge level={record.risk_level} excluded={isExcluded(record)} />
                <span className="text-sm text-slate-500">
                  {record.category_name ||
                    (record.category
                      ? t(`hse.risk.category.${record.category}`, { defaultValue: record.category })
                      : "")}
                </span>
              </div>
              <div className="mt-5 space-y-4">
                <Field label={t("hse.risk.fields.consequence")} value={record.consequence} />
                <Field label={t("hse.risk.fields.description")} value={record.description} />
                <Field label={t("hse.risk.fields.work_task")} value={record.work_task} />
                <Field label={t("hse.risk.fields.cause")} value={record.triggering_cause} />
                <Field label={t("hse.risk.fields.existing")} value={record.existing_measures} />
                <Field label={t("hse.risk.fields.history")} value={record.history} />
                <p className="text-xs text-slate-500">
                  {t("hse.risk.view.updated", { date: formatDisplayDate(record.updated_at) })}
                </p>
              </div>
            </div>

            <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3">
                <HseFieldInfoLabel
                  label={t("hse.risk.tiltak.how")}
                  infoTitle="hse.risk.info.tiltak.title"
                  infoBody="hse.risk.info.tiltak.body"
                />
              </div>
              {tiltak.length === 0 ? (
                <p className="text-sm text-slate-500">{t("hse.risk.tiltak.empty")}</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {tiltak.map((item) => (
                    <li key={item.id} className="py-2">
                      <Link
                        to={`/dashboard/hse-ks/tiltak/${item.id}`}
                        className="text-sm font-semibold text-[#1463C7] hover:underline"
                      >
                        {item.title}
                      </Link>
                      <span className="ml-2 text-xs text-slate-500">
                        {t(`hse.tiltak.status.${item.status || "planned"}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {canEdit && !isExcluded(record) ? (
                <button
                  type="button"
                  onClick={handleExclude}
                  className="mt-4 text-sm font-semibold text-slate-600 hover:text-slate-900"
                >
                  {t("hse.risk.exclude")}
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </HsePageChrome>
  );
};
